import { useCallback, useEffect, useRef, useState } from 'react'
import { useInterval } from './useInterval'

const POLL_INTERVAL = 5 * 60 * 1000

/** Floor between checks, so rapid tab switching does not refetch on every focus. */
const MIN_CHECK_GAP = 60 * 1000

interface DeployedVersion {
  buildId?: string
  /** Absent on older deploys, which always notified. */
  notify?: boolean
}

async function fetchDeployedVersion(): Promise<DeployedVersion | null> {
  const res = await fetch('/version.json', { cache: 'no-store' })

  // CloudFront rewrites unknown paths to index.html with a 200, so a missing or
  // stale-cached version.json arrives as HTML rather than as an error.
  if (!res.ok || !res.headers.get('content-type')?.includes('application/json'))
    return null

  return (await res.json()) as DeployedVersion
}

/**
 * Detects that a newer build has been deployed while this tab stayed open. The
 * bundle is not code-split, so a stale tab keeps running old code indefinitely
 * without ever failing a chunk load.
 *
 * A deploy can opt out of prompting (`SILENT_DEPLOY=true`), which leaves open
 * tabs on the old bundle until they are reloaded for some other reason.
 */
export function useVersionCheck() {
  const [isOutdated, setIsOutdated] = useState(false)
  const lastCheckedAt = useRef(0)

  const check = useCallback(() => {
    if (!import.meta.env.PROD || isOutdated) return
    if (Date.now() - lastCheckedAt.current < MIN_CHECK_GAP) return

    lastCheckedAt.current = Date.now()

    fetchDeployedVersion()
      .then(deployed => {
        if (!deployed?.buildId || deployed.notify === false) return
        if (deployed.buildId !== __BUILD_ID__) setIsOutdated(true)
      })
      .catch(() => {})
  }, [isOutdated])

  useInterval(check, POLL_INTERVAL)

  useEffect(() => {
    function onVisibilityChange() {
      if (document.visibilityState === 'visible') check()
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [check])

  return isOutdated
}
